import {
	getAttributeOrThrow,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import {
	addGwpVariant,
	cartSectionsAsString,
	getGwpVariantToAdd,
	getPayloadForLineAdd,
	getPayloadForLineSwap,
	type CartActionInput,
} from '@/scripts/core/features'
import { CartDrawer } from '@/scripts/cart/CartDrawer'
import { ProductFormGeneric } from '@/scripts/product-form-context/ProductFormGeneric'

export class ProductForm extends ProductFormGeneric {
	static htmlSelector = 'product-form'
	variantIdEl: HTMLInputElement
	ctaTextEl?: HTMLElement
	addToCartText: string = 'Add to Cart'
	soldOutText: string = 'Out of Stock'
	submitButton: HTMLButtonElement
	isSubscriptionUpsell = false
	formEl: HTMLFormElement

	constructor() {
		super()
		this.variantIdEl = qsRequired<HTMLInputElement>('[name="id"]', this)
		this.ctaTextEl = qsOptional('[data-uc-cta-text]', this)
		this.submitButton = qsRequired<HTMLButtonElement>('button', this)
		this.formEl = qsRequired<HTMLFormElement>('form', this)
		if (this.hasAttribute('data-uc-add-to-cart-text')) {
			this.addToCartText = getAttributeOrThrow(
				'data-uc-add-to-cart-text',
				this
			)
		}
		if (this.hasAttribute('data-uc-sold-out-text')) {
			this.soldOutText = getAttributeOrThrow(
				'data-uc-sold-out-text',
				this
			)
		}
		this.formEl.addEventListener('submit', this.onSubmitHandler.bind(this))
	}

	getVariantIdEl() {
		return this.variantIdEl
	}

	async updateKeyQuantity(key: string, quantity: number) {
		const config = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: `application/javascript`,
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify({
				id: key,
				quantity: quantity,
			}),
		}

		return await fetch(`/cart/change.js`, config)
	}

	async onSubmitHandler(evt: Event) {
		evt.preventDefault()
		const productFormSteps = this.getContext()?.productFormSteps

		if (
			productFormSteps &&
			this.getContext().stepsCompleted < productFormSteps.length
		) {
			this.getContext().initializeSteps()
			return
		}

		const cartUpsell = qsOptional('.cart-upsell')
		if (cartUpsell) {
			cartUpsell.classList.add('loading')
		}

		const upsellSettings = this.getContext().upsellSettings
		if (upsellSettings) {
			const response = await this.updateKeyQuantity(
				upsellSettings.updateItemKey,
				upsellSettings.updatedItemQuantity
			)
		}

		let url = this.isSubscriptionUpsell
			? window.routes.cart_change_url
			: window.routes.cart_add_url

		const cartDrawer = qsRequired<CartDrawer>('cart-drawer')

		const { sellingPlanId, currentVariant } = this.getContext()
		const item = sellingPlanId
			? {
					quantity: 1,
					id: currentVariant.id,
					selling_plan: sellingPlanId,
					price: currentVariant.price,
			  }
			: {
					quantity: 1,
					id: currentVariant.id,
					price: currentVariant.price,
			  }

		const itemWasRemoved =
			upsellSettings && upsellSettings.updatedItemQuantity === 0
		const items = itemWasRemoved
			? getPayloadForLineSwap(item, upsellSettings.startingPrice)
			: getPayloadForLineAdd(item)

		// resolved before the request below, while the cart still holds the
		// pre-add state this decision is based on
		const gwpInput: CartActionInput = itemWasRemoved
			? {
					type: 'line_swap',
					lineItem: {
						...item,
						removedItemPrice: upsellSettings.startingPrice,
					},
			  }
			: { type: 'line_add', lineItem: item }
		const gwpVariantId = getGwpVariantToAdd(gwpInput)

		const formData = {
			items,
			sections: cartSectionsAsString(),
			sections_url: window.location.pathname,
		}

		cartDrawer.setActiveElement(document.activeElement)
		const config = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: `application/javascript`,
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify(formData),
		}

		void (await fetch(`${url}`, config)
			.then((response) => response.json())
			.then(async (response) => {
				if (response.status) {
					return
				}

				let sections = response.sections
				if (gwpVariantId) {
					const gwpSections = await addGwpVariant(gwpVariantId)
					if (gwpSections) {
						sections = gwpSections
					}
				}

				cartDrawer.renderContents({
					sections,
					payload: {
						type: 'line_add',
						lineItem: item,
					},
				})
			})
			.catch((e) => {
				console.error(e)
			})
			.finally(() => {
				cartDrawer.classList.remove('is-empty')
			}))
	}
}
